// Install Draft.js before using this code:
// npm install draft-js

import React, { useState, useRef, useEffect, useImperativeHandle, useCallback } from 'react';
import {
  Editor,
  EditorState,
  RichUtils,
  convertToRaw,
  convertFromRaw,
  Modifier, // Import Modifier
  SelectionState,
  CompositeDecorator,
  // ContentState,
} from 'draft-js';
import 'draft-js/dist/Draft.css';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import 'draft-js/dist/Draft.css';

const DEFAULT_NOTE_RAW = {
  blocks: [
    {
      key: 'title',
      text: '',
      type: 'header-one',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    },
  ],
  entityMap: {},
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const isWordChar = (char) => /[A-Za-z0-9_]/.test(char);

const HighlightSpan = (props) => (
  <span className="nr-highlight">{props.children}</span>
);

const createHighlightDecorator = (query) => {
  if (!query) return null;
  const regex = new RegExp(escapeRegExp(query), 'gi');
  return new CompositeDecorator([
    {
      strategy: (contentBlock, callback) => {
        const text = contentBlock.getText();
        regex.lastIndex = 0;
        let match;
        while ((match = regex.exec(text)) !== null) {
          callback(match.index, match.index + match[0].length);
        }
      },
      component: HighlightSpan,
    },
  ]);
};

const findFirstMatchSelection = (contentState, query) => {
  if (!query) return null;
  const regex = new RegExp(escapeRegExp(query), 'i');
  const blocks = contentState.getBlockMap();
  for (const block of blocks.values()) {
    const text = block.getText();
    const match = regex.exec(text);
    if (match) {
      return SelectionState.createEmpty(block.getKey()).merge({
        anchorOffset: match.index,
        focusOffset: match.index + match[0].length,
      });
    }
  }
  return null;
};

const getWordCount = (contentState) => {
  const text = contentState.getPlainText().replace(/\s+/g, ' ').trim();
  if (!text) return 0;
  return text.split(' ').filter(Boolean).length;
};

const RichTextEditor = React.forwardRef(({ onSave, initialContent, onTitleChange, readOnly, onRefineStateChange, highlightQuery, framePaddingOffset, noteMeta, editorLabelAction, refinedEntities = [], onConceptClick, onRefinedStaticViewChange, onSeeEntitiesRelations }, ref) => {
  const highlightDecorator = React.useMemo(() => createHighlightDecorator(highlightQuery), [highlightQuery]);
  const createStateWithContent = useCallback((contentState) => {
    const firstKey = contentState.getFirstBlock().getKey();
    const selection = SelectionState.createEmpty(firstKey);
    const baseState = EditorState.createWithContent(contentState, highlightDecorator || null);
    return EditorState.forceSelection(baseState, selection);
  }, [highlightDecorator]);
  //console.log('WHITEHEAD', onSave);
  const [editorState, setEditorState] = useState(() => {
    if (initialContent) {
      return createStateWithContent(convertFromRaw(JSON.parse(initialContent)));
    }
    return createStateWithContent(convertFromRaw(DEFAULT_NOTE_RAW));
  });
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Title state
  const [title, setTitle] = useState("");
  const [isEditingRefined, setIsEditingRefined] = useState(false);
  const [bottomInset, setBottomInset] = useState(0);
  const [keyboardInset, setKeyboardInset] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(null);
  const [isNarrowViewport, setIsNarrowViewport] = useState(false);
  const isNewNote = !noteMeta || noteMeta.id === 'new' || noteMeta._forceOpen;
  const isRefined = noteMeta?.refined === true || noteMeta?.is_refined === true || noteMeta?.refine_state === 'refined';
  const showStaticRefinedView = isRefined && !isEditingRefined;

  // Removed handleAddNewNote as it is no longer used in the editor

  // Create a ref for the editor
  const editorRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const bottomInsetRef = useRef(0);
  useImperativeHandle(ref, () => ({
    focusEditor: () => {
      if (readOnly || showStaticRefinedView) return;
      editorRef.current?.focus();
    },
    saveDraft: async () => {
      if (readOnly || showStaticRefinedView) return { success: false, reason: 'read_only' };
      return saveContent('draft');
    },
    refineNote: async () => {
      if (readOnly || showStaticRefinedView) return { success: false, reason: 'read_only' };
      return saveContent('refine');
    },
  }));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => {
      setIsNarrowViewport(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setIsEditingRefined(false);
  }, [noteMeta?.id]);

  useEffect(() => {
    onRefinedStaticViewChange?.(showStaticRefinedView);
  }, [onRefinedStaticViewChange, showStaticRefinedView]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;

    const updateInset = () => {
      setIsNarrowViewport(window.innerWidth <= 768);
      const occluded = window.innerHeight - (viewport.height + viewport.offsetTop);
      const inset = Math.max(0, occluded);
      bottomInsetRef.current = inset;
      setBottomInset(inset);
      setKeyboardInset(viewport.height < window.innerHeight ? inset : 0);
      setViewportHeight(viewport.height);
    };

    updateInset();
    viewport.addEventListener('resize', updateInset);
    viewport.addEventListener('scroll', updateInset);
    return () => {
      viewport.removeEventListener('resize', updateInset);
      viewport.removeEventListener('scroll', updateInset);
    };
  }, []);

  useEffect(() => {
    console.log("Initial Content:", initialContent);
    if (initialContent) {
      try {
        const contentState = convertFromRaw(JSON.parse(initialContent));
        const normalizedContent = ensureTitleBlock(contentState);
        setEditorState(createStateWithContent(normalizedContent));
        const initialTitleText = normalizedContent.getFirstBlock().getText();
        setTitle(initialTitleText);
        if (onTitleChange) onTitleChange(initialTitleText);
        if (onRefineStateChange) {
          const wordCount = getWordCount(normalizedContent);
          onRefineStateChange(wordCount > 5);
        }
      } catch (error) {
        console.error("Error parsing initialContent:", error);
        setEditorState(createStateWithContent(convertFromRaw(DEFAULT_NOTE_RAW)));
        setTitle('');
        if (onTitleChange) onTitleChange('');
        if (onRefineStateChange) {
          onRefineStateChange(false);
        }
      }
    } else {
      setEditorState(createStateWithContent(convertFromRaw(DEFAULT_NOTE_RAW)));
      setTitle('');
      if (onTitleChange) onTitleChange('');
      if (onRefineStateChange) {
        onRefineStateChange(false);
      }
    }
  }, [initialContent, onTitleChange, onRefineStateChange, createStateWithContent]);

  useEffect(() => {
    if (!highlightDecorator) {
      setEditorState(prevState => EditorState.set(prevState, { decorator: null }));
      return;
    }
    setEditorState(prevState => {
      const decoratedState = EditorState.set(prevState, { decorator: highlightDecorator });
      const selection = findFirstMatchSelection(decoratedState.getCurrentContent(), highlightQuery);
      if (selection) {
        return EditorState.forceSelection(decoratedState, selection);
      }
      return decoratedState;
    });
  }, [highlightDecorator, highlightQuery]);

  const ensureTitleBlock = (contentState) => {
    const firstBlock = contentState.getFirstBlock();
    if (!firstBlock || firstBlock.getType() === 'header-one') {
      return contentState;
    }
    const firstKey = firstBlock.getKey();
    let selection = SelectionState.createEmpty(firstKey);
    selection = selection.merge({
      anchorOffset: 0,
      focusOffset: firstBlock.getLength(),
    });
    return Modifier.setBlockType(contentState, selection, 'header-one');
  };

  const handleKeyCommand = (command) => {
    const newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState) {
      setEditorState(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  const handleBeforeInput = (chars) => {
    const selection = editorState.getSelection();
    const content = editorState.getCurrentContent();
    const block = content.getBlockForKey(selection.getStartKey());
    const blockText = block.getText();
    const isBlockStart = selection.getStartOffset() === 0 && selection.getEndOffset() === 0;

    if (isBlockStart && blockText.length === 0 && block.getType() !== 'header-one' && /^[a-z]$/.test(chars)) {
      const nextContent = Modifier.insertText(content, selection, chars.toUpperCase());
      const nextState = EditorState.push(editorState, nextContent, 'insert-characters');
      setEditorState(EditorState.forceSelection(nextState, nextContent.getSelectionAfter()));
      return 'handled';
    }

    if (chars !== ' ') return 'not-handled';

    const text = block.getText();

    // Mapping shortcuts to block types
    const shortcuts = {
      '#': 'header-one',
      '##': 'header-two',
      '*': 'unordered-list-item',
      '-': 'unordered-list-item',
      '1.': 'ordered-list-item',
      '>': 'blockquote',
    };

    // Check if the current text matches a shortcut
    for (const [key, blockType] of Object.entries(shortcuts)) {
      if (text === key) {
        // If match, remove the shortcut text and change block type
        const newContent = Modifier.replaceText(
          content,
          selection.merge({
            anchorOffset: 0,
            focusOffset: key.length,
          }),
          ''
        );

        let newEditorState = EditorState.push(editorState, newContent, 'remove-range');
        newEditorState = RichUtils.toggleBlockType(newEditorState, blockType);
        setEditorState(newEditorState);
        return 'handled';
      }
    }
    return 'not-handled';
  };

  const blockStyleFn = (block) => {
    if (block.getType() === 'header-one') {
      return 'nr-header-block';
    }
    return 'nr-body-block';
  };

  const handleReturn = (event, state) => {
    const selection = state.getSelection();
    const content = state.getCurrentContent();
    const firstBlock = content.getFirstBlock();

    if (firstBlock && selection.getStartKey() === firstBlock.getKey() && firstBlock.getType() === 'header-one') {
      const splitContent = Modifier.splitBlock(content, selection);
      const afterSplitSelection = splitContent.getSelectionAfter();
      const normalized = Modifier.setBlockType(splitContent, afterSplitSelection, 'unstyled');
      const nextState = EditorState.push(state, normalized, 'split-block');
      setEditorState(EditorState.forceSelection(nextState, normalized.getSelectionAfter()));
      return 'handled';
    }

    return 'not-handled';
  };

  const syncTitleFromState = (state) => {
    const content = state.getCurrentContent();
    const firstBlock = content.getFirstBlock();
    const nextTitleText = firstBlock ? firstBlock.getText() : '';
    if (nextTitleText !== title) {
      setTitle(nextTitleText);
      if (onTitleChange) onTitleChange(nextTitleText);
    }
  };

  const handleChange = (nextState) => {
    const nextContent = nextState.getCurrentContent();
    setEditorState(nextState);
    // Draft.js also emits onChange for selection/cursor updates.
    // Mark dirty only when content actually changes.
    if (onRefineStateChange) {
      const wordCount = getWordCount(nextContent);
      onRefineStateChange(wordCount > 5);
    }
  };

  const scrollSelectionIntoView = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof window === 'undefined') return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.top === 0 && rect.bottom === 0)) return;
    const containerRect = container.getBoundingClientRect();
    const navOffset = window.innerWidth <= 768 && !keyboardInset ? 72 : 0;
    const keyboardOffset = keyboardInset ? Math.ceil(keyboardInset * 0.25) : 0;
    const padding = 16 + Math.ceil(bottomInsetRef.current) + navOffset + keyboardOffset;
    const relativeTop = rect.top - containerRect.top + container.scrollTop;
    const relativeBottom = rect.bottom - containerRect.top + container.scrollTop;
    const visibleTop = container.scrollTop;
    const visibleBottom = visibleTop + container.clientHeight - bottomInsetRef.current - navOffset - keyboardOffset;

    if (relativeBottom > visibleBottom - padding) {
      const delta = relativeBottom - visibleBottom + padding;
      container.scrollTop += delta;
      return;
    }
    if (relativeTop < visibleTop + padding) {
      const delta = relativeTop - visibleTop - padding;
      container.scrollTop += delta;
    }
  }, [keyboardInset]);

  useEffect(() => {
    if (readOnly || showStaticRefinedView) return;
    const handle = window.requestAnimationFrame(scrollSelectionIntoView);
    return () => window.cancelAnimationFrame(handle);
  }, [editorState, readOnly, showStaticRefinedView, scrollSelectionIntoView]);

  // Auto-save on content change with debounce - disabled for now to prevent errors
  // Will implement proper auto-save after addressing save endpoint

  const saveContent = async (saveMode = 'draft') => {
    syncTitleFromState(editorState);
    setIsSaving(true);
    const rawContent = JSON.stringify(
      convertToRaw(editorState.getCurrentContent())
    );
    let saveOutcome = { success: false, mode: saveMode };
    try {
      const result = await onSave(rawContent, saveMode);
      const success = typeof result === 'object' ? Boolean(result?.success) : Boolean(result);
      const noteId = typeof result === 'object' ? (result?.noteId || result?.id) : undefined;
      const refined = typeof result === 'object'
        ? (result?.is_refined === true || result?.refined === true || result?.refine_state === 'refined')
        : false;
      saveOutcome = { success, noteId, mode: saveMode, refined };
      if (success) {
        if (saveMode === 'refine' && (refined || noteMeta?.refined === true || noteMeta?.is_refined === true || noteMeta?.refine_state === 'refined')) {
          setIsEditingRefined(false);
        }
        setSaveMessage(`Saved`);
        // Auto-clear message after 2s
        setTimeout(() => setSaveMessage(''), 2000);
      } else {
        setSaveMessage('Error saving');
      }
    } catch (e) {
      setSaveMessage('Error saving');
      saveOutcome = { success: false, mode: saveMode };
    }
    setTimeout(() => setIsSaving(false), 500); // Small delay for visual feedback
    return saveOutcome;
  };

  // Check if editor has content
  const hasContent = () => {
    const contentState = editorState.getCurrentContent();
    return contentState.hasText() && contentState.getPlainText().trim().length > 0;
  };
  void hasContent;

  const staticConcepts = React.useMemo(() => {
    const seen = new Set();
    return (Array.isArray(refinedEntities) ? refinedEntities : [])
      .map((entity, index) => {
        const name = String(entity?.name || '').trim();
        if (!name) return null;
        const normalized = name.toLowerCase();
        if (seen.has(normalized)) return null;
        seen.add(normalized);
        return {
          id: entity?.id || `${normalized}-${index}`,
          name,
          type: entity?.type || 'Concept',
          normalized,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.name.length - a.name.length);
  }, [refinedEntities]);

  const renderStaticBlock = React.useCallback((text, blockKey) => {
    if (!text) return null;
    if (!staticConcepts.length) return text;
    const loweredText = text.toLowerCase();
    const parts = [];
    let cursor = 0;

    while (cursor < text.length) {
      let bestMatch = null;

      for (const concept of staticConcepts) {
        let fromIndex = loweredText.indexOf(concept.normalized, cursor);
        while (fromIndex !== -1) {
          const before = fromIndex > 0 ? text[fromIndex - 1] : '';
          const afterIndex = fromIndex + concept.normalized.length;
          const after = afterIndex < text.length ? text[afterIndex] : '';
          const boundaryOk = (!before || !isWordChar(before)) && (!after || !isWordChar(after));
          if (boundaryOk) {
            if (!bestMatch || fromIndex < bestMatch.start || (fromIndex === bestMatch.start && concept.name.length > bestMatch.name.length)) {
              bestMatch = {
                start: fromIndex,
                end: afterIndex,
                name: concept.name,
                type: concept.type,
                id: concept.id,
              };
            }
            break;
          }
          fromIndex = loweredText.indexOf(concept.normalized, fromIndex + 1);
        }
      }

      if (!bestMatch) {
        parts.push(<React.Fragment key={`${blockKey}-txt-${cursor}`}>{text.slice(cursor)}</React.Fragment>);
        break;
      }

      if (bestMatch.start > cursor) {
        parts.push(
          <React.Fragment key={`${blockKey}-txt-${cursor}`}>
            {text.slice(cursor, bestMatch.start)}
          </React.Fragment>
        );
      }

      parts.push(
        <button
          key={`${blockKey}-concept-${bestMatch.start}-${bestMatch.id}`}
          type="button"
          onClick={() => onConceptClick?.({ id: bestMatch.id, name: bestMatch.name, type: bestMatch.type })}
          style={{
            borderRadius: 8,
            border: '1px solid rgba(73, 32, 153, 0.22)',
            background: 'rgba(73, 32, 153, 0.1)',
            color: '#492099',
            fontSize: '0.92em',
            fontWeight: 700,
            lineHeight: 1.1,
            padding: '1px 6px',
            margin: '0 1px',
            cursor: 'pointer',
            verticalAlign: 'baseline'
          }}
        >
          {text.slice(bestMatch.start, bestMatch.end)}
        </button>
      );

      cursor = bestMatch.end;
    }

    return parts;
  }, [onConceptClick, staticConcepts]);

  useEffect(() => {
    if (!onRefineStateChange) return;
    if (showStaticRefinedView) {
      onRefineStateChange(false);
      return;
    }
    const wordCount = getWordCount(editorState.getCurrentContent());
    onRefineStateChange(wordCount > 5);
  }, [editorState, onRefineStateChange, showStaticRefinedView]);

  // Removed helper functions

  const baseBottomPadding = viewportHeight ? Math.round(viewportHeight * 0.16) + 24 : 164;
  const navHeight = isNarrowViewport && !keyboardInset ? 84 : 0;
  const baseFramePaddingX = isNarrowViewport ? 0 : 12;
  const baseFramePaddingTop = isNarrowViewport ? 0 : 12;
  const baseFramePaddingBottom = isNarrowViewport ? 0 : 6;
  const resolvedFramePaddingOffset = framePaddingOffset || {};
  const framePaddingLeft = baseFramePaddingX + (resolvedFramePaddingOffset.left || 0);
  const framePaddingRight = baseFramePaddingX + (resolvedFramePaddingOffset.right || 0);
  const framePaddingTop = baseFramePaddingTop + (resolvedFramePaddingOffset.top || 0);
  const framePaddingBottomBase = baseFramePaddingBottom + (resolvedFramePaddingOffset.bottom || 0);
  const framePaddingBottom = isNarrowViewport
    ? `calc(${framePaddingBottomBase}px + ${navHeight}px + 16px)`
    : `${framePaddingBottomBase}px`;
  const frameHeight = isNarrowViewport
    ? 'calc(100% - env(safe-area-inset-bottom))'
    : '100%';
  const noteLabel = isRefined ? 'REFINED NOTE' : (isNewNote ? 'DRAFT NOTE' : 'NOTE');

  return (
    <div style={{
      background: 'transparent',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'Roboto, sans-serif',
      position: 'relative', // For absolute positioning of FAB/menu
      overflow: 'hidden'
    }}>
      {isNarrowViewport && (
        <div className="nr-editor-bg" aria-hidden="true">
          <div className="nr-editor-orb nr-editor-orb--one" />
          <div className="nr-editor-orb nr-editor-orb--two" />
        </div>
      )}

      {/* Saving Indicator (Top Right - subtle) */}
      <div style={{
        position: 'absolute',
        top: 52,
        right: 40,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: '0.8rem',
        color: isSaving ? '#6B7280' : (saveMessage === 'Error saving' ? '#B91C1C' : '#492099'),
        opacity: (isSaving || saveMessage) ? 1 : 0,
        transition: 'opacity 0.3s ease',
        pointerEvents: 'none',
        zIndex: 10
      }}>
        {isSaving ? (
          <>
            <div className="spinner" style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid #D1C4E9', borderTopColor: '#6B7280' }} />
            <span>Saving...</span>
          </>
        ) : (
          <>
            {saveMessage === 'Error saving' ? (
              <XCircleIcon style={{ width: 14, height: 14 }} />
            ) : (
              <CheckCircleIcon style={{ width: 14, height: 14 }} />
            )}
            <span>{saveMessage}</span>
          </>
        )}
      </div>

      {/* Global Style Overrides for Draft.js */}
      <style>
        {`
          .nr-editor-bg {
            position: absolute;
            inset: 0;
            pointer-events: none;
          }
          .nr-editor-orb {
            position: absolute;
            border-radius: 999px;
            background: radial-gradient(circle, rgba(92, 59, 139, 0.18) 0%, rgba(92, 59, 139, 0) 70%);
            filter: blur(0.5px);
            opacity: 0.75;
          }
          .nr-editor-orb--one {
            width: 240px;
            height: 240px;
            top: -80px;
            right: -60px;
          }
          .nr-editor-orb--two {
            width: 280px;
            height: 280px;
            bottom: 120px;
            left: -140px;
          }
          .DraftEditor-root {
            height: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }
          .public-DraftEditor-content {
            min-height: 100%;
            caret-color: #492099;
            padding-top: 6px !important;
            max-width: 100%;
            box-sizing: border-box;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .public-DraftEditorPlaceholder-root {
            color: #9AA2AF;
            opacity: 0.9;
            font-weight: 600;
            font-size: 26px;
            line-height: 1.2;
            margin-top: 6px;
          }
          .editor-root.is-typing {
            opacity: 1 !important;
            transition: none !important;
          }
          .public-DraftEditorPlaceholder-hasFocus {
            display: block;
          }
          .public-DraftStyleDefault-header-one {
            font-weight: 600;
          }
          .public-DraftStyleDefault-block {
            margin: 0 !important;
            padding: 0 !important;
            max-width: 100%;
            overflow-wrap: anywhere;
            word-break: break-word;
          }
          .nr-header-block {
            font-size: 26px !important;
            font-weight: 600;
            line-height: 1.2 !important;
            margin: 0 0 0.55rem 0 !important;
          }
          .nr-body-block {
            font-size: 17px !important;
            line-height: 1.45 !important;
          }
          .nr-highlight {
            background: rgba(73, 32, 153, 0.18);
            border-radius: 4px;
            padding: 0 2px;
          }
          .public-DraftEditor-content > div:first-child {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          .nr-static-refined-shell {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .nr-static-refined-shell::-webkit-scrollbar {
            width: 0;
            height: 0;
          }
          .editor-shell {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .editor-shell::-webkit-scrollbar {
            width: 0;
            height: 0;
          }
        `}
      </style>

      {/* Editor Content - Clean, unified design */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          height: frameHeight,
          paddingTop: framePaddingTop,
          paddingRight: framePaddingRight,
          paddingLeft: framePaddingLeft,
          paddingBottom: framePaddingBottom,
          boxSizing: 'border-box',
          background: 'transparent',
          position: 'relative',
          zIndex: 1
        }}
      >
        <div
          className={`editor-shell editor-root${showStaticRefinedView ? ' nr-static-refined-shell' : ''}`}
          ref={scrollContainerRef}
          style={{
            height: '100%',
            width: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            minWidth: 0,
            minHeight: 0,
            background: isNarrowViewport ? '#ffffff' : 'transparent',
            opacity: 1,
            transition: 'none',
            position: 'relative',
            boxSizing: 'border-box',
            borderRadius: isNarrowViewport ? '20px' : '0',
            backgroundClip: 'padding-box',
            clipPath: 'none',
            border: isNarrowViewport
              ? (showStaticRefinedView ? 'none' : '1px solid rgba(73, 32, 153, 0.08)')
              : 'none',
            boxShadow: isNarrowViewport ? '0 16px 26px rgba(73, 32, 153, 0.08)' : 'none'
          }}
          onClick={() => {
            if (showStaticRefinedView) return;
            editorRef.current?.focus();
          }}
        >
          <div
            style={{
              padding: isNarrowViewport
                ? `14px 14px ${baseBottomPadding + Math.ceil(bottomInset)}px 14px`
                : `10px 12px ${baseBottomPadding + Math.ceil(bottomInset)}px 12px`,
              minHeight: '100%',
              maxWidth: isNarrowViewport ? 'none' : '620px',
              margin: '0 auto',
              width: '100%',
              boxSizing: 'border-box',
              fontSize: '17px',
              lineHeight: 1.45,
              color: '#1F2937',
              cursor: showStaticRefinedView ? 'default' : 'text',
              flex: 1
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                marginBottom: 8
              }}
            >
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  textTransform: 'uppercase',
                  fontSize: 12,
                  letterSpacing: '0.12em',
                  color: '#4f3f70',
                  fontWeight: 700,
                  lineHeight: 1.2
                }}
              >
                <span>{noteLabel}</span>
              </div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {showStaticRefinedView && typeof onSeeEntitiesRelations === 'function' && (
                  <button
                    type="button"
                    onClick={onSeeEntitiesRelations}
                    aria-label="See entities and relations"
                    title="See entities and relations"
                    style={{
                      border: 'none',
                      borderRadius: 999,
                      background: 'rgba(73, 32, 153, 0.05)',
                      color: '#492099',
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1,
                      cursor: 'pointer',
                      padding: '8px 11px',
                      textTransform: 'none',
                      letterSpacing: 0,
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    <img
                      src="/graph_icon.png"
                      alt=""
                      aria-hidden="true"
                      style={{ width: 26, height: 26, display: 'block' }}
                    />
                  </button>
                )}
                {isRefined && !isEditingRefined && !readOnly && (
                  <button
                    type="button"
                    onClick={() => setIsEditingRefined(true)}
                    aria-label="Edit refined note"
                    title="Edit refined note"
                    style={{
                      border: 'none',
                      background: 'none',
                      borderRadius: 999,
                      color: '#492099',
                      fontSize: 12,
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: '5px 8px',
                      display: 'inline-flex',
                      alignItems: 'center'
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path d="M4 20h4l9.7-9.7a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16v4Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                      <path d="m12.5 7.5 4 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </button>
                )}
                {isRefined && isEditingRefined && !readOnly && (
                  <button
                    type="button"
                    onClick={() => setIsEditingRefined(false)}
                    style={{
                      border: 'none',
                      background: 'none',
                      color: '#492099',
                      fontSize: 12,
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textUnderlineOffset: 3,
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    Undo Edit
                  </button>
                )}
                {!showStaticRefinedView ? (editorLabelAction || null) : null}
              </div>
            </div>
            {showStaticRefinedView ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 4 }}>
                {editorState.getCurrentContent().getBlocksAsArray().map((block) => {
                  const blockType = block.getType();
                  const blockText = block.getText();
                  if (!blockText.trim()) {
                    return <div key={block.getKey()} style={{ minHeight: 12 }} />;
                  }
                  const sharedStyle = {
                    margin: 0,
                    color: '#1F2937',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word'
                  };
                  if (blockType === 'header-one') {
                    return (
                      <h1 key={block.getKey()} style={{ ...sharedStyle, fontSize: 26, fontWeight: 600, lineHeight: 1.2 }}>
                        {renderStaticBlock(blockText, block.getKey())}
                      </h1>
                    );
                  }
                  return (
                    <p key={block.getKey()} style={{ ...sharedStyle, fontSize: 17, lineHeight: 1.5 }}>
                      {renderStaticBlock(blockText, block.getKey())}
                    </p>
                  );
                })}
              </div>
            ) : (
              <Editor
                ref={editorRef}
                editorState={editorState}
                onChange={handleChange}
                handleKeyCommand={handleKeyCommand}
                handleBeforeInput={handleBeforeInput}
                handleReturn={handleReturn}
                blockStyleFn={blockStyleFn}
                readOnly={readOnly}
                spellCheck={false}
                autoCorrect="on"
                autoComplete="on"
                autoCapitalize="sentences"
                onBlur={() => {
                  syncTitleFromState(editorState);
                }}
                placeholder="Start writing"
              />
            )}
          </div>
        </div>
      </div>


    </div >
  );
});



export default RichTextEditor;
